import debug from 'debug';
import moment from 'moment';
import router from '@/router';
import globals from '@/globals';

import logger from '../../lib/logger';
import isMobile from '../../lib/isMobile';
import getHandler from '../../lib/handleEvent';

import paymentLinkService from './paymentLink.service';

const log = debug('store:paymentLink');
const skeletonTimeout = globals().skeleton.checkout.timeout;

const handleEvent = getHandler(log);

function filterValue({ field, value }) {
  const filters = {
    cpf: value && value.replace(/[.-]/g, ''),
    zipCode: value && value.replace('-', ''),
  };
  return filters[field] || value;
}

function sanitizeUser({ user }) {
  return {
    cpf: filterValue({ field: 'cpf', value: user.cpf }),
    email: user.email,
    zipCode: filterValue({ field: 'zipCode', value: user.zipCode }),
  };
}

async function handleUnexpectedError(error) {
  logger.error(error);
  await router.replace({ name: 'payment-link-error' }).catch((err) => log('router error', err));
  throw error;
}

export default {
  namespaced: true,
  state: {
    loading: true,
    paymentLink: null,
    summarySkeleton: false,
    downPaymentValueSkeleton: false,
    installmentValueSkeleton: false,
    ccbUrl: null,
    user: {
      cpf: null,
      email: null,
      zipCode: null,
      addressNumber: null,
      addressComplement: null,
      addressStreet: null,
    },
    checkout: {
      zipCode: null,
      addressStreet: null,
    },
    stepSize: null,
    nextRoute: null,
    extraQueryRoute: null,
    options: null,
    downPaymentIndexSelected: null,
    downPaymentSelected: null,
    installmentIndexSelected: null,
    installmentSelected: null,
    atOnboarding: false,
    isLinkValid: true,
    retailerRetentionType: null,
    dueDateIndexSelected: 0,
  },
  actions: {
    async getPaymentLink({ dispatch, commit }, { paymentLinkId }) {
      try {
        const { data } = await paymentLinkService.getPaymentLink({
          paymentLinkId,
        });

        commit('setPaymentLink', { paymentLink: data });
        dispatch('setNextRoute');
      } catch (error) {
        if (error && error.response && [401, 403].includes(error.response.status)) {
          dispatch('auth/setToken', { token: null }, { root: true });
          router.replace({ name: 'login' }).catch((err) => log('router error', err));
        } else {
          await handleUnexpectedError(error);
        }
      }
    },
    async acceptTerms({ state, dispatch, commit }) {
      const { paymentLink } = state;

      try {
        await paymentLinkService.acceptPaymentLinkTerms({
          paymentLinkId: paymentLink._id,
        });

        commit('setPaymentLink', {
          paymentLink: {
            ...paymentLink,
            acceptedTerms: true,
          },
        });
      } catch (error) {
        if (error && error.response && [401, 403].includes(error.response.status)) {
          dispatch('auth/setToken', { token: null }, { root: true });
          router.replace({ name: 'login' }).catch((err) => log('router error', err));
        } else {
          await handleUnexpectedError(error);
        }
      }
    },
    async verifyCredit({ state, dispatch, commit }) {
      const { paymentLink, user } = state;

      try {
        const { data } = await paymentLinkService.applyForPaymentLink({
          paymentLinkId: paymentLink._id,
          userInfo: sanitizeUser({ user }),
          device: isMobile() ? 'mobile' : 'desktop',
        });

        commit('setPaymentLink', { paymentLink: data.paymentLink });
        commit('setOptions', { options: data.downPaymentOptions });
        dispatch('setNextRoute');
        return { creditApplyStatus: data.paymentLink.creditApplyStatus };
      } catch (error) {
        if (error && error.response && [401, 403].includes(error.response.status)) {
          dispatch('auth/setToken', { token: null }, { root: true });
          router.replace({ name: 'login' }).catch((err) => log('router error', err));
        } else if (error && error.response && error.response.data === 'Invalid zipCode') {
          return { inputError: 'zipCode' };
        } else if (error && error.response && error.response.data === 'Invalid email') {
          return { inputError: 'email' };
        } else {
          await handleUnexpectedError(error);
        }
        return null;
      }
    },
    async getPaymentLinkCheckout({ state, dispatch, commit }) {
      const { paymentLink } = state;
      try {
        const { data } = await paymentLinkService.getPaymentLinkCheckout({
          paymentLinkId: paymentLink._id,
        });
        commit('setPaymentLinkCheckout', { checkout: data });
      } catch (error) {
        if (error && error.response && [401, 403].includes(error.response.status)) {
          dispatch('auth/setToken', { token: null }, { root: true });
          router.replace({ name: 'login' }).catch((err) => log('router error', err));
        } else {
          await handleUnexpectedError(error);
        }
      }
    },
    async applyComplementForPaymentLink({ state, dispatch, commit }) {
      const { paymentLink, user } = state;
      const { addressNumber, addressComplement, addressStreet } = user;

      try {
        const { data } = await paymentLinkService.applyComplementForPaymentLink({
          paymentLinkId: paymentLink._id,
          addressStreet,
          addressNumber,
          addressComplement,
        });

        commit('setPaymentLink', { paymentLink: data.paymentLink });
        dispatch('setNextRoute');
      } catch (error) {
        if (error && error.response && [401, 403].includes(error.response.status)) {
          dispatch('auth/setToken', { token: null }, { root: true });
          router.replace({ name: 'login' }).catch((err) => log('router error', err));
        } else {
          await handleUnexpectedError(error);
        }
      }
    },
    async getOptions({ state, commit, dispatch }) {
      const { paymentLink } = state;

      try {
        const { data } = await paymentLinkService.getOptions({
          paymentLinkId: paymentLink._id,
        });
        commit('setOptions', data);
        commit('setDownPaymentIndex', { index: 0 });
      } catch (error) {
        if (error && error.response && [401, 403].includes(error.response.status)) {
          dispatch('auth/setToken', { token: null }, { root: true });
          await router.replace({ name: 'login' }).catch((err) => log('router error', err));
        } else {
          await handleUnexpectedError(error);
        }
      }
    },
    async authorizeCheckout({ state, dispatch }) {
      const { paymentLink, installmentSelected } = state;

      try {
        await paymentLinkService.authorize({
          apiKey: paymentLink.retailerId.toString(),
          optionId: installmentSelected._id,
          orderId: paymentLink.orderId,
        });

        await dispatch('getPaymentLink', { paymentLinkId: paymentLink._id });
      } catch (error) {
        if (error && error.response && [401, 403].includes(error.response.status)) {
          dispatch('auth/setToken', { token: null }, { root: true });
          await router.replace({ name: 'login' }).catch((err) => log('router error', err));
        } else {
          await handleUnexpectedError(error);
        }
      }
    },
    setNextRoute({ state, commit }) {
      const { paymentLink } = state;
      const {
        status,
        creditApplyStatus,
        transactionId,
        onboardingToken,
        pendingComplementAddress,
        expireAt,
      } = paymentLink;

      if (creditApplyStatus === 'DENIED') {
        return commit('setNextRoute', { nextRoute: 'payment-link-credit-denied' });
      }

      if (['EXPIRED', 'CANCELED'].includes(status)) {
        commit('setIsLinkValid', false);
      }

      if (['SENT', 'CONTRACTED'].includes(status)) {
        const now = new Date();
        const expireDate = new Date(expireAt);
        if (status === 'SENT' && expireDate < now) {
          commit('setIsLinkValid', false);
        }

        if (transactionId) {
          commit('setAtOnboarding');
          return commit('setNextRoute', {
            nextRoute: 'v2-onboarding-wrapper',
            extraQueryRoute: {
              transactionId,
              onboardingToken,
            },
          });
        }

        if (creditApplyStatus === 'PRE_APPROVED') {
          if (pendingComplementAddress) {
            return commit('setNextRoute', { nextRoute: 'payment-link-apply-complement' });
          }
          return commit('setNextRoute', { nextRoute: 'payment-link-select-option' });
        }

        return commit('setNextRoute', { nextRoute: 'payment-link-verify-credit' });
      }

      return 'payment-link-error';
    },
    setLoading({ commit }, { loading }) {
      commit('setLoading', loading);
    },
    setUserData({ commit }, { field, value }) {
      commit('setUserData', { field, value });
    },
    setDownPaymentIndex({ commit }, { index }) {
      commit('toggleDownPaymentValueSkeleton', true);
      commit('toggleSummarySkeleton', true);
      commit('toggleInstallmentValueSkeleton', true);
      setTimeout(() => {
        commit('toggleDownPaymentValueSkeleton', false);
        commit('toggleSummarySkeleton', false);
        commit('toggleInstallmentValueSkeleton', false);
      }, skeletonTimeout);
      commit('setDownPaymentIndex', { index });
    },
    setInstallmentIndex({ commit }, { index }) {
      commit('toggleSummarySkeleton', true);
      commit('toggleInstallmentValueSkeleton', true);
      setTimeout(() => {
        commit('toggleSummarySkeleton', false);
        commit('toggleInstallmentValueSkeleton', false);
      }, skeletonTimeout);
      commit('setInstallmentIndex', { index });
    },
    setAtOnboarding({ commit }) {
      commit('setAtOnboarding');
    },
    redirect({ state }, { name, query }) {
      if (state.isLinkValid) {
        router.replace({ name, query })
          .catch((err) => log('router error', err));
      } else {
        router.replace({ name: 'payment-link-expired', query })
          .catch((err) => log('router error', err));
      }
    },
    setDueDateIndex({ commit, state }, index) {
      const {
        options,
        downPaymentIndexSelected,
        installmentIndexSelected,
      } = state;
      if (options && downPaymentIndexSelected >= 0 && index >= 0) {
        const downPayment = options[downPaymentIndexSelected];
        if (downPayment && downPayment.options) {
          const dueDateOption = downPayment.options[index];
          handleEvent({ state, ev: 'onDueDateChanged', data: { dueDateOption } });

          if (typeof installmentIndexSelected === 'number'
            && installmentIndexSelected >= 0) {
            const installment = dueDateOption.installmentOptions[installmentIndexSelected];
            handleEvent({ state, ev: 'onInstallmentChanged', data: { installment } });
          }
        }
      }

      commit('toggleSummarySkeleton', true);
      commit('toggleInstallmentValueSkeleton', true);
      setTimeout(() => {
        commit('toggleSummarySkeleton', false);
        commit('toggleInstallmentValueSkeleton', false);
      }, skeletonTimeout);
      commit('setDueDateIndex', index);
    },
  },
  mutations: {
    setPaymentLink(state, { paymentLink }) {
      state.paymentLink = paymentLink;
      state.stepSize = paymentLink.withoutOnboarding ? 3 : 4;
    },
    setPaymentLinkCheckout(state, { checkout }) {
      state.checkout = checkout;
    },
    toggleSummarySkeleton(state, summarySkeleton) {
      state.summarySkeleton = summarySkeleton;
    },
    toggleDownPaymentValueSkeleton(state, downPaymentValueSkeleton) {
      state.downPaymentValueSkeleton = downPaymentValueSkeleton;
    },
    toggleInstallmentValueSkeleton(state, installmentValueSkeleton) {
      state.installmentValueSkeleton = installmentValueSkeleton;
    },
    setNextRoute(state, { nextRoute, extraQueryRoute }) {
      state.nextRoute = nextRoute;
      if (extraQueryRoute) state.extraQueryRoute = extraQueryRoute;
    },
    setUserData(state, { field, value }) {
      state.user[field] = value;
    },
    setOptions(state, { options, retentionOptions }) {
      if (retentionOptions
        && (retentionOptions.retailerRetentionType)) {
        const { downPaymentPercentage,
          downPaymentValue,
          economyValue,
          hasGracePeriod,
          maxOfMaxTotalAmount,
          minOfMinTotalAmount,
          options: optionsArray,
        } = retentionOptions['0'];
        const promotionalInstallments = optionsArray[0].installmentOptions.filter((option) => option.retentionType === 'promotional');
        const specialInstallments = optionsArray[0].installmentOptions.filter((option) => option.retentionType === 'special');
        const filteredInstallments = retentionOptions.retailerRetentionType === 'all' || retentionOptions.retailerRetentionType === 'promotional' ? promotionalInstallments : specialInstallments;
        state.options = [{
          downPaymentPercentage,
          downPaymentValue,
          economyValue,
          hasGracePeriod,
          maxOfMaxTotalAmount,
          minOfMinTotalAmount,
          options: [{
            gracePeriodDueDate: optionsArray[0].gracePeriodDueDate,
            installmentOptions: filteredInstallments,
            maxTotalAmount: optionsArray[0].maxTotalAmount,
            minTotalAmount: optionsArray[0].minTotalAmount,
          }],
        }];
        state.retailerRetentionType = retentionOptions.retailerRetentionType;
        return;
      }
      if (!options) return;
      state.options = options.sort((a, b) => a.downPaymentPercentage - b.downPaymentPercentage);
    },
    setDownPaymentIndex(state, { index }) {
      state.downPaymentIndexSelected = index;
      state.downPaymentSelected = state.options[index];

      if (state.installmentSelected) {
        state.installmentSelected = state
          .downPaymentSelected.options[state.installmentIndexSelected];
      }
    },
    setDueDateIndex(state, index) {
      state.dueDateIndexSelected = index;
    },
    setInstallmentIndex(state, { index }) {
      state.installmentIndexSelected = index;
      state.installmentSelected = state.downPaymentSelected.options[state.dueDateIndexSelected]
        .installmentOptions[index];
      state.ccbUrl = `${globals().apiUrl}/public/ccb/${state.installmentSelected?._id}`;
    },
    setLoading(state, loading) {
      state.loading = loading;
    },
    setAtOnboarding(state) {
      state.atOnboarding = true;
    },
    setIsLinkValid(state, validity) {
      state.isLinkValid = validity;
    },
  },
  getters: {
    dateOptions: ({ downPaymentIndexSelected, options }) => {
      const formattedDates = options[downPaymentIndexSelected].options.map(
        (dueDateOption) => ({
          date: moment(dueDateOption.gracePeriodDueDate, 'YYYY-MM-DD').toDate(),
          formattedDate: moment(dueDateOption.gracePeriodDueDate, 'YYYY-MM-DD')
            .format('D-MMM')
            .toUpperCase(),
        }),
      );
      // Sort the formattedDates array by the date property in ascending order
      // formattedDates.sort((a, b) => a.date - b.date);
      return formattedDates.map((dueDateOption) => dueDateOption.formattedDate.split('-'));
    },
  },
};
